import { useRef, useState, useCallback, useMemo } from 'react'
import { LogEntry, FailGroup } from './api'

export interface LogStats {
  success: number
  fail: number
  recovered: number
  captchaTotal: number
  error429: number
  firstSuccessTs: number
  successMessages: Map<string, number>
  failMessages: Map<string, number>
  recoveredMessages: Map<string, number>
  boosterOk: Map<string, number>
  boosterFail: Map<string, number>
  boosterTemp: Map<string, number>
  // Idea #69: SmartPilot skips — kept separate from ok/fail/temp.
  boosterSkip: Map<string, number>
  boosterOkCount?: number
  boosterFailCount?: number
  boosterTempCount?: number
  boosterSkipCount?: number
  okGrouped?: Record<string, any>
  failGrouped?: Record<string, FailGroup>
  tempGrouped?: Record<string, FailGroup>
  skipGrouped?: Record<string, FailGroup>
  // Profiler «данные по прокачке» — grouped by profile group/party.
  // Separate from booster okGrouped/failGrouped (different item shape) so the
  // booster Results path is never affected. Populated only for profiler cids.
  profilerOkGrouped?: Record<string, any>
  profilerFailGrouped?: Record<string, FailGroup>
  // Authoritative per-campaign profiler counters (persisted, same source as the
  // top result tiles). The grouped accumulators above reset on panel restart,
  // so Results headline totals use these to stay in sync with the dashboard.
  profilerOkCount?: number
  profilerFailCount?: number
  // Profiler captcha detail (proxy / type / difficulty / cycles) — from
  // profiler_campaign_stats[cid].captcha_detail. Captcha tab only.
  profilerCaptchaDetail?: {
    solved?: number
    cycles_sum?: number
    profiles_ok?: number
    profile_cap_sum?: number
    profiles_with_cap?: number
    by_proxy?: Record<string, {
      count?: number
      cycles_sum?: number
      types?: Record<string, number>
      difficulties?: Record<string, number>
    }>
    by_type?: Record<string, number>
    by_difficulty?: Record<string, number>
  }
}

const ITERATION_SUCCESS = [
  'успешно создан', 'успешно прокачен', 'ЗАВЕРШЁН',
]
const ITERATION_FAIL = [
  'iteration failed', 'consecutive failures',
  'no usable fingerprint', 'не найден',
  'Нет профилей', 'Нет доступных',
]
const MID_ERROR = [
  'blank page', 'timed out', 'timeout', 'retry',
  'reloading', 'respawn', 'backoff', 'ERR_CONNECTION',
  'ERR_ADDRESS', 'unreachable', 'failed', 'не сохранён', 'ЧАСТИЧНО',
]

const CAPTCHA_SOLVE_START = 'CAPTCHA solve start'

function has(msg: string, keywords: string[]): boolean {
  const ml = msg.toLowerCase()
  return keywords.some(kw => ml.includes(kw.toLowerCase()))
}

function dateFromTs(ts: number): string {
  const d = new Date(ts * 1000)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = d.getFullYear()
  return `${dd}-${mm}-${yy}`
}

function successLabel(msg: string): string {
  const clean = msg.replace(/^\[W\d+\]\s*/, '')
  if (/Воркер завершен успешно/i.test(clean)) return 'Успешный клик'
  if (/profile created/i.test(clean)) return 'Профиль создан'
  return 'Успешная итерация'
}

function failLabel(msg: string): string {
  const clean = msg.replace(/^\[W\d+\]\s*/, '')
  if (/no usable fingerprint/i.test(clean)) return 'Не найден подходящий fingerprint'
  if (/no profiles/i.test(clean)) return 'Нет профилей в БД для прокачки'
  if (/iteration error/i.test(clean)) return 'Ошибка итерации'
  if (/iteration.*failed/i.test(clean)) return 'Итерация провалена'
  if (/exhausted/i.test(clean)) return 'Исчерпаны попытки'
  if (/consecutive failures/i.test(clean)) return 'Серия последовательных ошибок'
  return normalize(clean)
}

function normalize(msg: string): string {
  return msg
    .replace(/^\[W\d+\]\s*/, '')
    .replace(/PID=\d+/g, 'PID=N')
    .replace(/pid[= ]\d+/gi, 'pid=N')
    .replace(/\b\d{5,}\b/g, 'NUM')
    .trim()
}

function addMsg(map: Map<string, number>, key: string) {
  if (!key) return
  map.set(key, (map.get(key) || 0) + 1)
}

function createEmpty(): LogStats {
  return {
    success: 0, fail: 0, recovered: 0, captchaTotal: 0, error429: 0,
    firstSuccessTs: 0,
    successMessages: new Map(),
    failMessages: new Map(),
    recoveredMessages: new Map(),
    boosterOk: new Map(),
    boosterFail: new Map(),
    boosterTemp: new Map(),
    boosterSkip: new Map(),
  }
}

function parseBoosterResult(msg: string): { status: 'OK' | 'FAIL' | 'TEMP' | 'SKIP', label: string } | null {
  const m = msg.match(/BOOSTER_RESULT:(OK|FAIL|TEMP|SKIP):(.+)/)
  if (!m) return null
  return { status: m[1] as 'OK' | 'FAIL' | 'TEMP' | 'SKIP', label: m[2].trim() }
}

function isBoosterLog(msg: string): boolean {
  return msg.includes('BOOSTER_RESULT:') || msg.includes('Nakrutka ')
    || msg.includes('STEP ') || msg.includes('SmartPilot')
    || msg.includes('Click OK:') || msg.includes('Click attempt')
    || msg.includes('Click FAILED') || msg.includes('SERP page')
    || msg.includes('DOUBLE_CLICK') || msg.includes('Multisession')
    || msg.includes('Site walk') || msg.includes('Prewarm')
}

function ingestBatch(stats: LogStats, entries: LogEntry[]) {
  for (const l of entries) {
    const msg = l.message

    if (msg.includes(CAPTCHA_SOLVE_START)) {
      stats.captchaTotal++
    }

    if (msg.includes('Яндекс 429') && msg.includes('BOOSTER_RESULT')) {
      stats.error429++
    }

    const br = parseBoosterResult(msg)
    if (br) {
      if (br.status === 'OK') {
        addMsg(stats.boosterOk, br.label)
      } else if (br.status === 'TEMP') {
        addMsg(stats.boosterTemp, br.label)
      } else if (br.status === 'SKIP') {
        addMsg(stats.boosterSkip, br.label)  // Idea #69
      } else {
        addMsg(stats.boosterFail, br.label)
      }
      continue
    }

    if (isBoosterLog(msg)) continue

    if (has(msg, ITERATION_SUCCESS)) {
      stats.success++
      if (stats.firstSuccessTs === 0) stats.firstSuccessTs = Date.now()
      addMsg(stats.successMessages, `${successLabel(msg)} | ${dateFromTs(l.time)}`)
      continue
    }

    if (l.level === 'ERROR' || l.level === 'CRITICAL') {
      if (has(msg, ITERATION_FAIL)) {
        stats.fail++
        addMsg(stats.failMessages, failLabel(msg))
      } else {
        stats.recovered++
        addMsg(stats.recoveredMessages, normalize(msg))
      }
      continue
    }

    if (l.level === 'WARNING' && has(msg, MID_ERROR)) {
      stats.recovered++
      addMsg(stats.recoveredMessages, normalize(msg))
    }
  }
}

/**
 * Accumulated stats hook — survives log trimming.
 * Call `ingest(entries)` with each new batch of log entries.
 * Call `reset()` to zero everything out.
 */
export function useAccumulatedStats() {
  const ref = useRef<LogStats>(createEmpty())
  const [ver, setVer] = useState(0)

  const ingest = useCallback((entries: LogEntry[]) => {
    if (entries.length === 0) return
    ingestBatch(ref.current, entries)
    setVer(v => v + 1)
  }, [])

  const reset = useCallback(() => {
    ref.current = createEmpty()
    setVer(v => v + 1)
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stats: LogStats = useMemo(() => ({
    success: ref.current.success,
    fail: ref.current.fail,
    recovered: ref.current.recovered,
    captchaTotal: ref.current.captchaTotal,
    error429: ref.current.error429,
    firstSuccessTs: ref.current.firstSuccessTs,
    successMessages: ref.current.successMessages,
    failMessages: ref.current.failMessages,
    recoveredMessages: ref.current.recoveredMessages,
    boosterOk: ref.current.boosterOk,
    boosterFail: ref.current.boosterFail,
    boosterTemp: ref.current.boosterTemp,
    boosterSkip: ref.current.boosterSkip,
  }), [ver])

  return { stats, ingest, reset }
}

export function computeStatsFromLogs(entries: LogEntry[]): LogStats {
  const s = createEmpty()
  ingestBatch(s, entries)
  return s
}
